import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import SearchableLedgerDropdown from '../../Common/SearchableLedgerDropdown';

const PaymentModesGrid = ({
  paymentModes = [],
  onPaymentModesChange,
  targetAmount = 0,
  errors = {},
  disabled = false
}) => {
  const [modes, setModes] = useState(paymentModes);
  useEffect(() => {
    setModes(paymentModes);
  }, [paymentModes]);

  const paymentMethodOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'card', label: 'Card' },
    { value: 'credit_card', label: 'Credit Card' }
  ];

  // Map payment method → accountClassification for SearchableLedgerDropdown
  const getAccountClassification = (method) => {
    if (method === 'cash') return 'CASHBOOK';
    if (method === 'bank_transfer') return 'BANK';
    if (method === 'cheque') return 'BANK';
    if (method === 'card') return 'BANK';
    if (method === 'credit_card') return 'BANK';
    return undefined;
  };

  const getAccountClassificationLabel = (method) => {
    if (method === 'cash') return 'Cashbook only';
    if (method === 'bank_transfer') return 'Bank only';
    if (method === 'cheque') return 'Bank only';
    if (method === 'card') return 'Bank only';
    if (method === 'credit_card') return 'Bank only';
    return '';
  };

  const handleAddPaymentMode = () => {
    const newMode = {
      id: Date.now(),
      paymentMethod: 'cash',
      amount: 0,
      ledgerAccount: '',
      ledgerAccountLabel: '',
      chequeDetails: { chequeNo: '', chequeDate: '', bankName: '' },
      transferDetails: { referenceNo: '', transferDate: '' },
      notes: ''
    };
    const updated = [...modes, newMode];
    setModes(updated);
    onPaymentModesChange(updated);
  };

  const handleRemovePaymentMode = (index) => {
    if (modes.length <= 1) {
      toast.error('At least one payment mode is required');
      return;
    }
    const updated = modes.filter((_, i) => i !== index);
    setModes(updated);
    onPaymentModesChange(updated);
  };

  const handleModeChange = (index, field, value) => {
    const updated = [...modes];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updated[index] = {
        ...updated[index],
        [parent]: { ...updated[index][parent], [child]: value }
      };
    } else if (field === 'paymentMethod') {
      // Reset ledger when method changes — different account type needed
      updated[index] = {
        ...updated[index],
        paymentMethod: value,
        ledgerAccount: '',
        ledgerAccountLabel: ''
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setModes(updated);
    onPaymentModesChange(updated);
  };

  const handleLedgerChange = (index, accountId, label) => {
    const updated = [...modes];
    updated[index] = {
      ...updated[index],
      ledgerAccount: accountId || '',
      ledgerAccountLabel: label || ''
    };
    setModes(updated);
    onPaymentModesChange(updated);
  };

  const getTotalAmount = () => modes.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
  const getRemainingAmount = () => targetAmount - getTotalAmount();
  const isValidAmount = () => Math.abs(getTotalAmount() - targetAmount) < 0.01;

  useEffect(() => {
    if (paymentModes.length === 0 && targetAmount > 0) handleAddPaymentMode();
  }, [targetAmount]);

  const inputCls = `w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed`;
  const labelCls = `block text-xs font-medium text-gray-600 mb-1`;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5">
        <span className="text-xs font-semibold text-white">Payment Modes</span>
        <button
          onClick={handleAddPaymentMode}
          disabled={disabled}
          className="flex items-center gap-1 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs text-white transition-colors disabled:opacity-50"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Mode
        </button>
      </div>

      <div className="px-3 py-2 space-y-2">
        {modes.map((mode, index) => (
          <div key={mode.id || index} className="border border-gray-200 rounded px-3 py-2 relative bg-gray-50/40">
            {/* Remove button */}
            {modes.length > 1 && !disabled && (
              <button
                onClick={() => handleRemovePaymentMode(index)}
                className="absolute top-1.5 right-1.5 text-gray-300 hover:text-red-500 transition-colors"
                title="Remove"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            )}

            {/* Row 1: Method | Amount | Ledger Account */}
            <div className="grid grid-cols-3 gap-2 pr-4 mb-1.5">
              <div>
                <label className={labelCls}>Method <span className="text-red-500">*</span></label>
                <select
                  value={mode.paymentMethod}
                  onChange={(e) => handleModeChange(index, 'paymentMethod', e.target.value)}
                  disabled={disabled}
                  className={inputCls}
                >
                  {paymentMethodOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Amount <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  value={mode.amount}
                  onChange={(e) => handleModeChange(index, 'amount', parseFloat(e.target.value) || 0)}
                  disabled={disabled}
                  step="0.01"
                  min="0"
                  className={`${inputCls} text-right`}
                  placeholder="0.00"
                />
              </div>

              {/* Ledger Account — filtered by method classification */}
              <div>
                <label className={labelCls}>
                  Ledger Account <span className="text-red-500">*</span>
                  <span className="ml-1 text-[10px] text-indigo-400 font-normal">
                    ({getAccountClassificationLabel(mode.paymentMethod)})
                  </span>
                </label>
                <SearchableLedgerDropdown
                  key={`ledger-${mode.id || index}-${mode.paymentMethod}`}
                  value={mode.ledgerAccount}
                  displayLabel={mode.ledgerAccountLabel}
                  onChange={(accountId, label) => handleLedgerChange(index, accountId, label)}
                  accountClassification={getAccountClassification(mode.paymentMethod)}
                  placeholder="Select ledger"
                  disabled={disabled}
                  error={!!errors[`paymentMode_${index}_ledger`]}
                  menuPlacement="down"
                />
                {errors[`paymentMode_${index}_ledger`] && (
                  <p className="text-[10px] text-red-500 mt-0.5">{errors[`paymentMode_${index}_ledger`]}</p>
                )}
              </div>

            </div>

            {/* Row 2: method-specific details + Notes */}
            {mode.paymentMethod === 'cheque' && (
              <div className="grid grid-cols-3 gap-2 pr-4">
                <div>
                  <label className={labelCls}>Cheque No. <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={mode.chequeDetails.chequeNo}
                    onChange={(e) => handleModeChange(index, 'chequeDetails.chequeNo', e.target.value)}
                    disabled={disabled}
                    className={inputCls}
                    placeholder="Cheque number"
                  />
                </div>
                <div>
                  <label className={labelCls}>Cheque Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={mode.chequeDetails.chequeDate}
                    onChange={(e) => handleModeChange(index, 'chequeDetails.chequeDate', e.target.value)}
                    disabled={disabled}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Notes</label>
                  <input
                    type="text"
                    value={mode.notes}
                    onChange={(e) => handleModeChange(index, 'notes', e.target.value)}
                    disabled={disabled}
                    className={inputCls}
                    placeholder="Notes…"
                  />
                </div>
              </div>
            )}

            {mode.paymentMethod === 'bank_transfer' && (
              <div className="grid grid-cols-3 gap-2 pr-4">
                <div>
                  <label className={labelCls}>Transfer Reference <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={mode.transferDetails.referenceNo}
                    onChange={(e) => handleModeChange(index, 'transferDetails.referenceNo', e.target.value)}
                    disabled={disabled}
                    className={inputCls}
                    placeholder="Reference number"
                  />
                </div>
                <div>
                  <label className={labelCls}>Transfer Date</label>
                  <input
                    type="date"
                    value={mode.transferDetails.transferDate}
                    onChange={(e) => handleModeChange(index, 'transferDetails.transferDate', e.target.value)}
                    disabled={disabled}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Notes</label>
                  <input
                    type="text"
                    value={mode.notes}
                    onChange={(e) => handleModeChange(index, 'notes', e.target.value)}
                    disabled={disabled}
                    className={inputCls}
                    placeholder="Notes…"
                  />
                </div>
              </div>
            )}

            {!['cheque', 'bank_transfer'].includes(mode.paymentMethod) && (
              <div className="grid grid-cols-3 gap-2 pr-4">
                <div className="col-span-3">
                  <label className={labelCls}>Notes</label>
                  <input
                    type="text"
                    value={mode.notes}
                    onChange={(e) => handleModeChange(index, 'notes', e.target.value)}
                    disabled={disabled}
                    className={inputCls}
                    placeholder="Notes…"
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Allocation summary strip */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded text-xs">
          <span className="text-gray-500">Target: <span className="font-semibold text-gray-700">{targetAmount.toFixed(2)}</span></span>
          <span className="text-gray-500">Allocated: <span className="font-semibold text-indigo-700">{getTotalAmount().toFixed(2)}</span></span>
          <span className={`font-semibold ${getRemainingAmount() === 0 ? 'text-green-700' : 'text-red-600'}`}>
            Remaining: {getRemainingAmount().toFixed(2)}
          </span>
          {isValidAmount() && (
            <span className="text-green-600 font-medium">✓ Balanced</span>
          )}
        </div>

        {!isValidAmount() && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            {getRemainingAmount() > 0
              ? `Allocate remaining ${getRemainingAmount().toFixed(2)}`
              : `Reduce allocation by ${Math.abs(getRemainingAmount()).toFixed(2)}`}
          </p>
        )}

        {errors.paymentModes && (
          <p className="text-xs text-red-600">{errors.paymentModes}</p>
        )}
      </div>
    </div>
  );
};

export default PaymentModesGrid;
